export interface Claim {
    id: number;
    name?: string;
    description?: string;
}

export interface GroupedClaim<T extends Claim> {
    action: string;
    claim: T;
}

export interface ClaimGroup<T extends Claim> {
    baseName: string;
    description?: string;
    claims: GroupedClaim<T>[];
}

/**
 * Groups claims by their base name and categorizes them by action type
 * @param claims - Array of claims to group
 * @returns Array of claim groups organized by base name
 */
export function groupClaimsByBaseName<T extends Claim>(claims: T[]): ClaimGroup<T>[] {
    if (!claims || claims.length === 0) {
        return [];
    }

    const groupedMap = new Map<string, GroupedClaim<T>[]>();
    const descriptionMap = new Map<string, string>();

    claims.forEach(claim => {
        const claimName = claim.name || '';
        const claimDescription = claim.description || '';
        const { action, baseName } = extractActionAndBaseName(claimName);
        const baseDescription = extractActionAndBaseName(claimDescription).baseName;

        if (!groupedMap.has(baseName)) {
            groupedMap.set(baseName, []);
            descriptionMap.set(baseName, baseDescription);
        }

        groupedMap.get(baseName)?.push({
            action,
            claim,
        });
    });

    // Convert map to array and sort by base name
    return Array.from(groupedMap.entries())
        .map(([baseName, claims]) => ({
            baseName,
            description: descriptionMap.get(baseName),
            claims: claims.sort(
                (a, b) => getActionPriority(a.action) - getActionPriority(b.action)
            ),
        }))
        .sort((a, b) => a.baseName.localeCompare(b.baseName));
}

/**
 * Extracts the action and base name from a string
 * @param string - The string (e.g., "Create Internal Transfer/Loan Payment")
 * @returns Object containing action and base name
 */
export function extractActionAndBaseName(string: string): { action: string; baseName: string } {
    // CONVENTION:Generic pattern to match any word followed by a space and the rest of the description
    const actionPattern = /^(\w+)\s+(.+)$/;
    const match = string.match(actionPattern);

    if (match) {
        return {
            action: match[1],
            baseName: match[2],
        };
    }

    // If no action pattern is found, treat the entire description as base name
    return {
        action: '',
        baseName: string,
    };
}

/**
 * Returns priority for sorting actions in a logical order
 * @param action - The action type
 * @returns Priority number (lower = higher priority)
 */
export function getActionPriority(action: string): number {
    const priorities: Record<string, number> = {
        Create: 1,
        Edit: 2,
        Approve: 3,
        Delete: 4,
    };

    return priorities[action] || 999;
}

/**
 * Extracts the base name from a description by removing the action prefix
 * @param string - The claim description
 * @returns The base name without the action prefix
 */
export function getBaseNameFromString(string: string): string {
    const { baseName } = extractActionAndBaseName(string);
    return baseName;
}

/**
 * Gets the display label for a claim based on whether it has an action
 * @param claim - The claim object
 * @param action - Optional action string
 * @returns The display label
 */
export function getClaimDisplayLabel<T extends Claim>(claim: T, action?: string): string {
    const name = claim.name || '';
    return action || name;
}
